import React, { useState, useEffect, useRef } from 'react';
import { Activity, Play, Pause, RotateCcw, TrendingUp, Calendar, Target } from 'lucide-react';
import './stepCounter.css';

const StepCounterApp = () => {
  const [steps, setSteps] = useState(0);
  const [isTracking, setIsTracking] = useState(false);
  const [dailyGoal, setDailyGoal] = useState(10000);
  const [distance, setDistance] = useState(0);
  const [calories, setCalories] = useState(0);
  const [lastAcceleration, setLastAcceleration] = useState({ x: 0, y: 0, z: 0 });
  const [isSupported, setIsSupported] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [duration, setDuration] = useState(0);
  
  const stepThreshold = 1.2;
  const stepCooldown = 300; // milliseconds
  const lastStepTime = useRef(0);
  const intervalRef = useRef(null);

  // Check device motion support
  useEffect(() => {
    if (window.DeviceMotionEvent) {
      setIsSupported(true);
    }
  }, []);

  // Request permission for iOS devices
  const requestPermission = async () => {
    if (typeof DeviceMotionEvent.requestPermission === 'function') {
      try {
        const permission = await DeviceMotionEvent.requestPermission();
        if (permission === 'granted') {
          setPermissionGranted(true);
          return true;
        }
      } catch (error) {
        console.error('Permission request failed:', error);
        return false;
      }
    } else {
      setPermissionGranted(true);
      return true;
    }
    return false;
  };

  // Calculate magnitude of acceleration vector
  const calculateMagnitude = (x, y, z) => {
    return Math.sqrt(x * x + y * y + z * z);
  };

  // Detect step based on acceleration changes
  const detectStep = (acceleration) => {
    const currentMagnitude = calculateMagnitude(
      acceleration.x || 0,
      acceleration.y || 0,
      acceleration.z || 0
    );
    
    const lastMagnitude = calculateMagnitude(
      lastAcceleration.x,
      lastAcceleration.y,
      lastAcceleration.z
    );

    const deltaAcceleration = Math.abs(currentMagnitude - lastMagnitude);
    const currentTime = Date.now();

    if (deltaAcceleration > stepThreshold && 
        (currentTime - lastStepTime.current) > stepCooldown) {
      lastStepTime.current = currentTime;
      return true;
    }
    return false;
  };

  // Handle device motion event
  const handleDeviceMotion = (event) => {
    if (!isTracking) return;

    const acceleration = event.accelerationIncludingGravity;
    if (acceleration) {
      if (detectStep(acceleration)) {
        setSteps(prevSteps => {
          const newSteps = prevSteps + 1;
          // Update distance (approximate: 0.7 meters per step)
          setDistance((newSteps * 0.0007).toFixed(2)); // in km
          // Update calories (approximate: 0.04 calories per step)
          setCalories(Math.round(newSteps * 0.04));
          return newSteps;
        });
      }

      setLastAcceleration({
        x: acceleration.x || 0,
        y: acceleration.y || 0,
        z: acceleration.z || 0
      });
    }
  };

  // Start tracking
  const startTracking = async () => {
    if (!isSupported) {
      alert('Device motion is not supported on this device/browser');
      return;
    }

    const hasPermission = await requestPermission();
    if (!hasPermission) {
      alert('Motion permission is required for step tracking');
      return;
    }

    setIsTracking(true);
    setStartTime(Date.now());
    
    // Add event listener
    window.addEventListener('devicemotion', handleDeviceMotion, true);

    // Start duration timer
    intervalRef.current = setInterval(() => {
      if (startTime) {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setDuration(elapsed);
      }
    }, 1000);
  };

  // Stop tracking
  const stopTracking = () => {
    setIsTracking(false);
    window.removeEventListener('devicemotion', handleDeviceMotion, true);
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  // Reset all data
  const resetData = () => {
    setSteps(0);
    setDistance(0);
    setCalories(0);
    setDuration(0);
    setStartTime(null);
    if (isTracking) {
      stopTracking();
    }
  };

  // Format duration
  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate progress percentage
  const progressPercentage = Math.min((steps / dailyGoal) * 100, 100);

  // Calculate circle progress
  const radius = 88;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progressPercentage / 100) * circumference;

  // API call to save step data (replace with your backend endpoint)
  const saveStepData = async () => {
    try {
      const stepData = {
        steps,
        distance,
        calories,
        duration,
        date: new Date().toISOString().split('T')[0],
        timestamp: new Date().toISOString()
      };

      // Replace with your actual API endpoint
      const response = await fetch('/api/steps', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Add your auth headers here if needed
          // 'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(stepData)
      });

      if (response.ok) {
        alert('Step data saved successfully!');
      } else {
        throw new Error('Failed to save step data');
      }
    } catch (error) {
      console.error('Error saving step data:', error);
      alert('Failed to save step data. Please try again.');
    }
  };

  return (
    <div className="step-counter-container">
      <div className="step-counter-app">
        {/* Header */}
        <div className="app-header">
          <div className="header-content">
            <div className="header-title">
              <Activity className="icon large" />
              <h1>Step Counter</h1>
            </div>
            <div className="header-date">
              {new Date().toLocaleDateString()}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="main-content">
          {/* Step Counter Circle */}
          <div className="step-counter-circle">
            <svg className="progress-ring">
              <circle
                cx="96"
                cy="96"
                r={radius}
                className="progress-ring-bg"
              />
              <circle
                cx="96"
                cy="96"
                r={radius}
                className="progress-ring-fill"
                style={{
                  strokeDasharray: circumference,
                  strokeDashoffset: strokeDashoffset
                }}
              />
            </svg>
            <div className="step-display">
              <div className="step-count">{steps.toLocaleString()}</div>
              <div className="step-label">steps</div>
              <div className="step-progress">
                {Math.round(progressPercentage)}% of goal
              </div>
            </div>
          </div>

          {/* Goal Setting */}
          <div className="goal-section">
            <div className="goal-header">
              <div className="goal-title">
                <Target className="icon" />
                <span>Daily Goal</span>
              </div>
            </div>
            <input
              type="number"
              value={dailyGoal}
              onChange={(e) => setDailyGoal(Number(e.target.value))}
              className="goal-input"
              min="1000"
              max="50000"
              step="1000"
            />
          </div>

          {/* Stats Grid */}
          <div className="stats-grid">
            <div className="stat-card distance">
              <div className="stat-value distance">{distance}</div>
              <div className="stat-label distance">km</div>
            </div>
            <div className="stat-card calories">
              <div className="stat-value calories">{calories}</div>
              <div className="stat-label calories">cal</div>
            </div>
            <div className="stat-card time">
              <div className="stat-value time">{formatDuration(duration)}</div>
              <div className="stat-label time">time</div>
            </div>
          </div>

          {/* Support Status */}
          {!isSupported && (
            <div className="alert error">
              <p className="alert-text">
                Device motion is not supported on this device/browser.
              </p>
            </div>
          )}

          {isSupported && !permissionGranted && !isTracking && (
            <div className="alert warning">
              <p className="alert-text">
                Permission required. Click "Start Tracking" to enable motion sensors.
              </p>
            </div>
          )}

          {/* Control Buttons */}
          <div className="button-container">
            <button
              onClick={isTracking ? stopTracking : startTracking}
              disabled={!isSupported}
              className={`main-button ${isTracking ? 'stop' : 'start'}`}
            >
              {isTracking ? <Pause className="icon" /> : <Play className="icon" />}
              <span>{isTracking ? 'Stop Tracking' : 'Start Tracking'}</span>
            </button>

            <div className="secondary-buttons">
              <button
                onClick={resetData}
                className="secondary-button reset"
              >
                <RotateCcw className="icon small" />
                <span>Reset</span>
              </button>
              <button
                onClick={saveStepData}
                disabled={steps === 0}
                className="secondary-button save"
              >
                <TrendingUp className="icon small" />
                <span>Save</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepCounterApp;